import React, { useState, useEffect } from 'react';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdk0sJ0dJM2XvEC8QolawCS2_XE9uwo8XdLSFwkDUIB4nZanw/viewform?usp=sharing&ouid=102917319616543510588';
export const GOOGLE_FORM_EMBED_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdk0sJ0dJM2XvEC8QolawCS2_XE9uwo8XdLSFwkDUIB4nZanw/viewform?embedded=true';

export const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      id="feedback-modal-overlay"
      className="fixed inset-0 z-[250] flex items-center justify-center p-2.5 sm:p-6 bg-slate-950/85 backdrop-blur-md overflow-hidden animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-modal-title"
    >
      <div
        id="feedback-card"
        className="bg-slate-900 border border-white/15 rounded-2xl sm:rounded-3xl shadow-2xl max-w-2xl w-full h-[90vh] sm:h-[85vh] max-h-[850px] flex flex-col relative overflow-hidden text-white"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top ambient color bar */}
        <div className="h-1 w-full bg-gradient-to-r from-amber-400 via-indigo-500 to-teal-400 shrink-0" />

        {/* Modal Header */}
        <div className="p-3.5 sm:p-5 border-b border-white/10 flex items-center justify-between gap-3 shrink-0 bg-slate-950/40">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 text-base shrink-0 shadow-sm">
              ⭐
            </div>
            <div className="min-w-0">
              <h2 id="feedback-modal-title" className="text-sm sm:text-base font-bold text-white tracking-tight truncate flex items-center gap-2">
                <span>User Feedback</span>
                <span className="text-[10px] uppercase font-semibold tracking-wider px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 shrink-0">
                  Google Form
                </span>
              </h2>
              <p className="text-[11px] sm:text-xs text-slate-400 truncate">
                Rate your musical experience and share ideas to help us improve!
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Direct External Link Button */}
            <a
              id="open-form-external-tab-btn"
              href={GOOGLE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] sm:text-xs bg-slate-800 hover:bg-slate-700 text-indigo-300 hover:text-white border border-slate-700 hover:border-slate-600 px-2.5 sm:px-3 py-1.5 rounded-xl transition flex items-center gap-1.5 font-medium shadow-sm active:scale-95"
              title="Open Google Form in a new browser tab"
            >
              <span className="hidden sm:inline">Open in Tab</span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 21 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </a>

            {/* Close Button */}
            <button
              id="close-feedback-modal-btn"
              onClick={onClose}
              className="w-8 h-8 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition border border-white/10 active:scale-95"
              aria-label="Close Feedback Modal"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Form Container with responsive iframe */}
        <div className="flex-1 relative w-full bg-slate-950 overflow-hidden">
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-950 z-10 text-slate-400">
              <div className="w-8 h-8 border-3 border-amber-400 border-t-transparent rounded-full animate-spin" />
              <p className="text-xs font-medium">Loading Google Form...</p>
            </div>
          )}

          <iframe
            id="google-feedback-form-frame"
            src={GOOGLE_FORM_EMBED_URL}
            title="Kinetic Harmony Feedback Form"
            className="w-full h-full border-0"
            onLoad={() => setIsLoading(false)}
            loading="lazy"
          />
        </div>

        {/* Modal Footer */}
        <div className="p-3 sm:p-4 border-t border-white/10 bg-slate-950/80 flex items-center justify-between gap-3 shrink-0">
          <p className="text-[11px] text-slate-400 hidden sm:block">
            Form submits directly to Google Forms • Thank you for your feedback!
          </p>
          <div className="flex items-center gap-2 ml-auto">
            <a
              href={GOOGLE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-amber-400 hover:text-amber-300 underline underline-offset-2 px-2 py-1 font-medium sm:hidden"
            >
              Open external tab ↗
            </a>
            <button
              id="feedback-done-btn"
              onClick={onClose}
              className="text-xs bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-4 py-2 rounded-xl transition shadow-md shadow-indigo-600/30 active:scale-95"
            >
              Done / Return to Music
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
